import {
  NPC_BOX_CLEARANCE,
  NPC_BOX_INTERVAL_TICKS,
  NPC_BOX_MAX,
  NPC_BOX_RADIUS,
  NPC_BOX_TIER_WEIGHT,
  NPC_MASS,
  NPC_RADIUS,
} from './constants';
import { isFree } from './obstacles';
import { hashRandom } from './rng';
import { tileCenter } from './tilemap';
import type { NpcBox, NpcTier, PlayerId, State, Vec2 } from './types';
import { dist } from './vec';

/** Enumerating legal centres guarantees a spawn whenever any valid tile remains. */
export function stepNpcBoxes(s: State): void {
  if (s.tick < s.nextNpcBoxTick) return;
  s.nextNpcBoxTick = s.tick + NPC_BOX_INTERVAL_TICKS;
  if (s.npcBoxes.length >= NPC_BOX_MAX) return;
  const occupied = [...s.players, ...s.crates, ...s.npcs, ...s.pickups, ...s.spawnPlan, ...s.npcBoxes];
  const candidates: Vec2[] = [];
  for (let y = 0; y < s.map.height; y++) {
    for (let x = 0; x < s.map.width; x++) {
      const pos = tileCenter(x, y);
      if (!isFree(s.map, s.pillars, pos.x, pos.y, NPC_BOX_RADIUS)) continue;
      if (occupied.some(o => dist(pos, o.pos) < NPC_BOX_CLEARANCE)) continue;
      if (s.arenaSetup.starts.some(start => dist(pos, start) < NPC_BOX_CLEARANCE)) continue;
      candidates.push(pos);
    }
  }
  if (candidates.length === 0) return;
  const pos = candidates[Math.floor(hashRandom(s.rng, 2 * s.tick) * candidates.length)];
  const total = NPC_BOX_TIER_WEIGHT.reduce((sum, weight) => sum + weight, 0);
  let roll = hashRandom(s.rng, 2 * s.tick + 1) * total;
  let tier: NpcTier = 1;
  for (const weight of NPC_BOX_TIER_WEIGHT) {
    if (roll < weight) break;
    roll -= weight;
    tier = (tier + 1) as NpcTier;
  }
  // At most one spawn per tick; negative IDs cannot collide with a released NPC's ID.
  s.npcBoxes.push({ id: -s.tick - 1, pos, radius: NPC_BOX_RADIUS, tier, hp: tier });
}

/** The last hit owns the helper; opening never consumes the arena RNG. */
export function hitNpcBox(s: State, box: NpcBox, ownerId: PlayerId): void {
  if (--box.hp > 0) return;
  s.npcBoxes = s.npcBoxes.filter(b => b !== box);
  s.npcs.push({
    id: s.nextNpcId++, ownerId, tier: box.tier, pos: { ...box.pos },
    vel: { x: 0, y: 0 }, radius: NPC_RADIUS, mass: NPC_MASS,
    fireCooldownTicks: 0, moveDir: { x: 0, y: 0 },
  });
}
